import {
  ComponentProps,
  ReactNode,
  RefObject,
  createContext,
  useContext,
  useRef,
  useState,
} from 'react';
import { CalendarIcon } from 'lucide-react';
import { clsx } from 'clsx';
import { Calendar } from '@/components/ui/Calendar.tsx';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/ui/Popover.tsx';
import PrimitiveInput from '@/components/form/_primitives/PrimitiveInput.tsx';
import RangeMonthCaption from '@/components/form/_primitives/dateRange/RangeMonthCaption.tsx';
import { useMaskedDateInput } from '@/components/form/_primitives/dateRange/useMaskedDateInput.ts';
import { useIsMobile } from '@/hooks/use-mobile.tsx';

export type DateRange = {
  from: Date | undefined;
  to: Date | undefined;
};

type Side = 'start' | 'end';

type DateRangeContextValue = {
  value: DateRange;
  onChange: (range: DateRange) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  activeSide: Side;
  openFromSide: (side: Side) => void;
  startRef: RefObject<HTMLDivElement | null>;
  endRef: RefObject<HTMLDivElement | null>;
  disabled?: boolean;
};

const DateRangeContext = createContext<DateRangeContextValue | undefined>(
  undefined,
);

const useDateRangeContext = () => {
  const ctx = useContext(DateRangeContext);
  if (!ctx) {
    throw new Error(
      'PrimitiveDateRangeInput.Start/End must be rendered inside PrimitiveDateRangeInput.Root',
    );
  }
  return ctx;
};

const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);

type RootProps = {
  value: DateRange;
  onChange: (value: DateRange) => void;
  disabled?: boolean;
  children: ReactNode;
};

const Root = ({ value, onChange, disabled, children }: RootProps) => {
  const [open, setOpen] = useState(false);
  const [activeSide, setActiveSide] = useState<Side>('start');
  const [pickedStart, setPickedStart] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date>(
    () => value.from ?? new Date(),
  );
  const startRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const openFromSide = (side: Side) => {
    if (disabled) return;
    setActiveSide(side);
    setPickedStart(false);
    if (side === 'start' && value.from) {
      setViewMonth(startOfMonth(value.from));
    }
    if (side === 'end' && value.to) {
      const left = new Date(value.to);
      left.setMonth(left.getMonth() - 1);
      setViewMonth(startOfMonth(left));
    }
    setOpen(true);
  };

  const handleOpenChange = (next: boolean) => {
    if (disabled) return;
    setOpen(next);
    if (next) setPickedStart(false);
  };

  const handleSelect = (range: DateRange | undefined, triggerDate: Date) => {
    if (!pickedStart) {
      // First click — only set `from`, leave `to` undefined so only one day highlights
      onChange({ from: triggerDate, to: undefined });
      setPickedStart(true);
      return;
    }
    // Second click — commit the range as react-day-picker computed it (handles from<to ordering)
    const next = range ?? { from: undefined, to: undefined };
    onChange(next);
    setPickedStart(false);
    setOpen(false);
  };

  const handleCaptionMonthPick = (side: 'left' | 'right', monthIdx: number) => {
    const base = new Date(viewMonth);
    if (side === 'left') {
      base.setMonth(monthIdx);
    } else {
      base.setMonth(monthIdx - 1);
    }
    base.setDate(1);
    setViewMonth(base);
  };

  const handleCaptionYearPick = (side: 'left' | 'right', year: number) => {
    const base = new Date(viewMonth);
    if (side === 'left') {
      base.setFullYear(year);
    } else {
      const rightMonthIdx = (viewMonth.getMonth() + 1) % 12;
      const adjustedYear = rightMonthIdx === 0 ? year - 1 : year;
      base.setFullYear(adjustedYear);
    }
    base.setDate(1);
    setViewMonth(base);
  };

  // Always anchor to the start input so the popover position is stable when
  // the user switches between start and end inputs.
  const anchorRef = startRef;

  const ctxValue: DateRangeContextValue = {
    value,
    onChange,
    open,
    setOpen,
    activeSide,
    openFromSide,
    startRef,
    endRef,
    disabled,
  };

  return (
    <DateRangeContext.Provider value={ctxValue}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        {children}
        <PopoverAnchor
          virtualRef={
            anchorRef as RefObject<{ getBoundingClientRect(): DOMRect }>
          }
        />
        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          <Calendar
            mode="range"
            numberOfMonths={isMobile ? 1 : 2}
            captionLayout="label"
            month={viewMonth}
            onMonthChange={setViewMonth}
            selected={value}
            onSelect={handleSelect}
            components={{
              MonthCaption: ({ calendarMonth, displayIndex }) => (
                <RangeMonthCaption
                  month={calendarMonth.date}
                  side={displayIndex === 0 ? 'left' : 'right'}
                  onMonthPick={handleCaptionMonthPick}
                  onYearPick={handleCaptionYearPick}
                />
              ),
            }}
          />
        </PopoverContent>
      </Popover>
    </DateRangeContext.Provider>
  );
};

type SideInputProps = {
  error?: string;
} & Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'type'>;

type SideInputInternalProps = SideInputProps & {
  side: Side;
};

const SideInput = ({
  side,
  error,
  className,
  placeholder,
  onBlur,
  ...rest
}: SideInputInternalProps) => {
  const ctx = useDateRangeContext();
  const ref = side === 'start' ? ctx.startRef : ctx.endRef;
  const sideValue = side === 'start' ? ctx.value.from : ctx.value.to;
  const handleSideChange = (date: Date | undefined) => {
    if (side === 'start') {
      ctx.onChange({ ...ctx.value, from: date });
    } else {
      ctx.onChange({ ...ctx.value, to: date });
    }
  };

  const { text, handleTextChange, handleBlur } = useMaskedDateInput(
    sideValue,
    handleSideChange,
    onBlur,
  );

  const open = () => ctx.openFromSide(side);

  return (
    <div ref={ref} className="relative w-full">
      <PrimitiveInput
        {...rest}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder ?? 'DD.MM.YYYY'}
        error={error}
        disabled={ctx.disabled ?? rest.disabled}
        value={text}
        onChange={handleTextChange}
        onBlur={handleBlur}
        onFocus={open}
        className={clsx('pr-9', className)}
      />
      <button
        type="button"
        disabled={ctx.disabled ?? rest.disabled}
        aria-label="Otwórz kalendarz"
        onClick={open}
        className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center justify-center rounded-sm p-1 text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <CalendarIcon size={18} />
      </button>
    </div>
  );
};

const Start = (props: SideInputProps) => <SideInput {...props} side="start" />;
const End = (props: SideInputProps) => <SideInput {...props} side="end" />;

const PrimitiveDateRangeInput = { Root, Start, End };

export default PrimitiveDateRangeInput;
